<template>
  <div class="data-platform-page">
    <!-- Hero Banner -->
    <section class="hero-banner">
      <div class="hero-overlay"></div>
      <svg class="hero-svg" width="500" height="200" viewBox="0 0 500 200">
        <circle cx="250" cy="100" r="30" fill="#fff" />
        <circle cx="100" cy="50" r="20" fill="#00B96B" />
        <circle cx="100" cy="150" r="20" fill="#FF9500" />
        <circle cx="400" cy="50" r="20" fill="#7C3AED" />
        <circle cx="400" cy="150" r="20" fill="#0891B2" />
        <line x1="250" y1="100" x2="100" y2="50" stroke="#fff" stroke-width="1" opacity="0.5" />
        <line x1="250" y1="100" x2="100" y2="150" stroke="#fff" stroke-width="1" opacity="0.5" />
        <line x1="250" y1="100" x2="400" y2="50" stroke="#fff" stroke-width="1" opacity="0.5" />
        <line x1="250" y1="100" x2="400" y2="150" stroke="#fff" stroke-width="1" opacity="0.5" />
      </svg>
      <div class="hero-content">
        <h1 class="hero-title">数据中台</h1>
        <p class="hero-sub">数据集成 · 数据治理 · 数据分析 · 数据可视化</p>
      </div>
    </section>

    <!-- Tab Content -->
    <section class="tab-section">
      <div class="content-container">
        <!-- Tabs -->
        <div class="tab-bar">
          <button
            v-for="(tab, index) in tabs"
            :key="index"
            class="tab-btn"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Integration Tab -->
        <div v-if="activeTab === 'integration'" class="tab-content">
          <div class="feature-grid">
            <div v-for="(feature, i) in integrationFeatures" :key="i" class="feature-card">
              <div class="feature-icon-wrap" :style="{ background: feature.iconBg }">
                <Icon :icon="feature.icon" :size="22" :color="feature.iconColor" />
              </div>
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-desc">{{ feature.desc }}</p>
            </div>
          </div>
          <!-- Connector logos -->
          <div class="connector-section">
            <h3 class="connector-title">支持的20+数据连接器</h3>
            <div class="connector-grid">
              <div
                v-for="(connector, i) in connectors"
                :key="i"
                class="connector-item"
              >
                {{ connector }}
              </div>
            </div>
          </div>
          <!-- Pricing -->
          <div class="pricing-bar">
            <div class="pricing-info">
              <h3 class="pricing-name">数据集成模块</h3>
              <p class="pricing-price-text">从¥3,980/年起，支持按数据量计费</p>
            </div>
            <div class="pricing-actions">
              <button class="btn-outline" @click="showTrialModal">在线咨询</button>
              <button class="btn-primary" @click="showTrialModal">立即购买</button>
            </div>
          </div>
        </div>

        <!-- Governance Tab -->
        <div v-if="activeTab === 'governance'" class="tab-content">
          <div class="feature-grid">
            <div v-for="(feature, i) in governanceFeatures" :key="i" class="feature-card">
              <div class="feature-icon-wrap" :style="{ background: feature.iconBg }">
                <Icon :icon="feature.icon" :size="22" :color="feature.iconColor" />
              </div>
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-desc">{{ feature.desc }}</p>
            </div>
          </div>
          <!-- Governance Flow -->
          <div class="flow-section">
            <h3 class="flow-title">数据治理流程</h3>
            <div class="flow-steps">
              <div v-for="(step, i) in governanceFlow" :key="i" class="flow-step">
                <div class="flow-icon-wrap" :style="{ background: step.iconBg }">
                  <Icon :icon="step.icon" :size="24" :color="step.iconColor" />
                </div>
                <p class="flow-label">{{ step.label }}</p>
              </div>
              <template v-for="i in governanceFlow.length - 1" :key="'arrow-' + i">
                <div class="flow-arrow">
                  <Icon icon="lucide:arrow-right" :size="20" color="#D1D5DB" />
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Analytics Tab -->
        <div v-if="activeTab === 'analytics'" class="tab-content">
          <div class="feature-grid">
            <div v-for="(feature, i) in analyticsFeatures" :key="i" class="feature-card">
              <div class="feature-icon-wrap" :style="{ background: feature.iconBg }">
                <Icon :icon="feature.icon" :size="22" :color="feature.iconColor" />
              </div>
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-desc">{{ feature.desc }}</p>
            </div>
          </div>
          <!-- Dashboard Preview -->
          <div class="dashboard-preview">
            <h3 class="preview-title">分析看板预览</h3>
            <div class="preview-grid">
              <div class="preview-card">
                <div class="preview-header">
                  <span class="preview-label">销售趋势</span>
                  <span class="preview-tag tag-green">+23.5%</span>
                </div>
                <div class="preview-bars">
                  <div v-for="(h, i) in [40, 55, 70, 60, 85, 100]" :key="i" class="bar" :style="{ height: h + '%', opacity: h / 100 + 0.2 }"></div>
                </div>
              </div>
              <div class="preview-card">
                <div class="preview-header">
                  <span class="preview-label">用户画像</span>
                  <span class="preview-tag tag-brand">5维度</span>
                </div>
                <div class="preview-radar">
                  <svg width="100" height="100" viewBox="0 0 100 100">
                    <polygon points="50,10 90,35 80,85 20,85 10,35" fill="none" stroke="#E5E7EB" stroke-width="1" />
                    <polygon points="50,25 75,40 70,75 30,75 25,40" fill="rgba(0,102,255,.2)" stroke="#0066FF" stroke-width="1.5" />
                  </svg>
                </div>
              </div>
              <div class="preview-card">
                <div class="preview-header">
                  <span class="preview-label">转化漏斗</span>
                  <span class="preview-tag tag-amber">5步</span>
                </div>
                <div class="preview-funnel">
                  <div class="funnel-bar" :style="{ width: '90%', opacity: 1 }"></div>
                  <div class="funnel-bar" :style="{ width: '72%', opacity: 0.8 }"></div>
                  <div class="funnel-bar" :style="{ width: '55%', opacity: 0.6 }"></div>
                  <div class="funnel-bar" :style="{ width: '38%', opacity: 0.4 }"></div>
                  <div class="funnel-bar" :style="{ width: '22%', opacity: 0.2 }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Visualization Tab -->
        <div v-if="activeTab === 'visualization'" class="tab-content">
          <div class="feature-grid">
            <div v-for="(feature, i) in visualizationFeatures" :key="i" class="feature-card">
              <div class="feature-icon-wrap" :style="{ background: feature.iconBg }">
                <Icon :icon="feature.icon" :size="22" :color="feature.iconColor" />
              </div>
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-desc">{{ feature.desc }}</p>
            </div>
          </div>
          <!-- Big Screen Preview -->
          <div class="bigscreen-preview">
            <h3 class="bigscreen-title">销售运营实时大屏</h3>
            <div class="bigscreen-grid">
              <div class="bigscreen-stat">
                <p class="stat-label-light">今日销售额</p>
                <p class="stat-value-cyan">¥1,286,580</p>
                <div class="mini-bars">
                  <div v-for="(h, i) in [30, 50, 70, 90, 60]" :key="i" class="mini-bar cyan" :style="{ height: h + '%' }"></div>
                </div>
              </div>
              <div class="bigscreen-stat">
                <p class="stat-label-light">活跃用户</p>
                <p class="stat-value-green">8,592</p>
                <div class="stat-sub-row">
                  <div class="stat-sub-item">
                    <span class="sub-label">新增</span>
                    <span class="sub-val green">328</span>
                  </div>
                  <div class="stat-sub-item">
                    <span class="sub-label">付费</span>
                    <span class="sub-val amber">156</span>
                  </div>
                  <div class="stat-sub-item">
                    <span class="sub-label">留存</span>
                    <span class="sub-val purple">72%</span>
                  </div>
                </div>
              </div>
              <div class="bigscreen-stat">
                <p class="stat-label-light">订单分布</p>
                <div class="distribution-bars">
                  <div class="dist-row">
                    <span class="dist-label">商城</span>
                    <div class="dist-bar-bg"><div class="dist-bar-fill cyan" :style="{ width: '65%' }"></div></div>
                    <span class="dist-val cyan">65%</span>
                  </div>
                  <div class="dist-row">
                    <span class="dist-label">官网</span>
                    <div class="dist-bar-bg"><div class="dist-bar-fill green" :style="{ width: '20%' }"></div></div>
                    <span class="dist-val green">20%</span>
                  </div>
                  <div class="dist-row">
                    <span class="dist-label">其他</span>
                    <div class="dist-bar-bg"><div class="dist-bar-fill amber" :style="{ width: '15%' }"></div></div>
                    <span class="dist-val amber">15%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="content-container">
        <div class="cta-card">
          <div class="cta-content">
            <h3 class="cta-title">数据中台，一站式数据解决方案</h3>
            <p class="cta-desc">数据集成 · 治理 · 分析 · 可视化，助力企业数据驱动决策</p>
          </div>
          <div class="cta-actions">
            <button class="btn-outline-cta" @click="showTrialModal">在线咨询</button>
            <button class="btn-primary-cta" @click="showTrialModal">
              <Icon icon="lucide:rocket" :size="18" color="#fff" /> 免费试用
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { Icon } from '@/components/Icon'
import { usePortalState } from '../usePortalState'

defineOptions({ name: 'DataPlatformPage' })

const { showTrialModal } = usePortalState()

const activeTab = ref('integration')

const tabs = [
  { id: 'integration', label: '数据集成' },
  { id: 'governance', label: '数据治理' },
  { id: 'analytics', label: '数据分析' },
  { id: 'visualization', label: '数据可视化' }
]

interface FeatureItem {
  title: string
  desc: string
  icon: string
  iconColor: string
  iconBg: string
}

const integrationFeatures: FeatureItem[] = [
  { title: '多源接入', desc: '支持MySQL/Oracle/SQLServer等20+数据源', icon: 'lucide:git-merge', iconColor: '#0066FF', iconBg: '#EBF5FF' },
  { title: '实时同步', desc: 'CDC实时数据变更捕获，毫秒级延迟', icon: 'lucide:zap', iconColor: '#00B96B', iconBg: '#ECFDF5' },
  { title: '离线同步', desc: '定时批量数据同步，支持增量/全量', icon: 'lucide:clock', iconColor: '#FF9500', iconBg: '#FFFBEB' },
  { title: 'ETL清洗', desc: '可视化ETL流程设计，拖拽式配置', icon: 'lucide:filter', iconColor: '#7C3AED', iconBg: '#F3E8FF' },
  { title: '血缘追踪', desc: '数据血缘可视化，全链路影响分析', icon: 'lucide:git-branch', iconColor: '#0891B2', iconBg: '#ECFEFF' },
  { title: '任务调度', desc: 'DAG任务编排，依赖管理和告警', icon: 'lucide:calendar-clock', iconColor: '#E11D48', iconBg: '#FFF1F2' }
]

const governanceFeatures: FeatureItem[] = [
  { title: '数据标准', desc: '建立企业数据标准体系，统一数据定义', icon: 'lucide:ruler', iconColor: '#0066FF', iconBg: '#EBF5FF' },
  { title: '质量监控', desc: '数据质量规则配置，自动检测和告警', icon: 'lucide:shield-check', iconColor: '#00B96B', iconBg: '#ECFDF5' },
  { title: '安全管控', desc: '数据分级分类，脱敏加密，权限管控', icon: 'lucide:lock', iconColor: '#FF9500', iconBg: '#FFFBEB' },
  { title: '元数据管理', desc: '技术/业务元数据统一管理', icon: 'lucide:layers', iconColor: '#7C3AED', iconBg: '#F3E8FF' },
  { title: '资产目录', desc: '数据资产盘点，目录化检索', icon: 'lucide:folder-tree', iconColor: '#0891B2', iconBg: '#ECFEFF' },
  { title: '生命周期', desc: '数据归档、销毁策略管理', icon: 'lucide:recycle', iconColor: '#E11D48', iconBg: '#FFF1F2' }
]

const analyticsFeatures: FeatureItem[] = [
  { title: 'BI报表', desc: '拖拽式报表设计，丰富的图表组件', icon: 'lucide:bar-chart-3', iconColor: '#0066FF', iconBg: '#EBF5FF' },
  { title: '自助分析', desc: '业务人员自助查询分析，无需SQL', icon: 'lucide:search', iconColor: '#00B96B', iconBg: '#ECFDF5' },
  { title: 'SQL工作台', desc: '在线SQL编辑器，语法高亮和自动补全', icon: 'lucide:terminal', iconColor: '#FF9500', iconBg: '#FFFBEB' },
  { title: '数据挖掘', desc: '聚类/分类/回归等算法库', icon: 'lucide:brain', iconColor: '#7C3AED', iconBg: '#F3E8FF' },
  { title: '机器学习', desc: '模型训练、评估和部署', icon: 'lucide:cpu', iconColor: '#0891B2', iconBg: '#ECFEFF' },
  { title: '预测分析', desc: '时间序列预测、异常检测', icon: 'lucide:trending-up', iconColor: '#E11D48', iconBg: '#FFF1F2' }
]

const visualizationFeatures: FeatureItem[] = [
  { title: '大屏设计', desc: '拖拽式大屏编辑器，所见即所得', icon: 'lucide:monitor', iconColor: '#0066FF', iconBg: '#EBF5FF' },
  { title: '组件库', desc: '50+图表组件，地图、3D组件', icon: 'lucide:shapes', iconColor: '#00B96B', iconBg: '#ECFDF5' },
  { title: '实时刷新', desc: 'WebSocket实时数据推送更新', icon: 'lucide:refresh-cw', iconColor: '#FF9500', iconBg: '#FFFBEB' },
  { title: '多屏适配', desc: 'PC/移动/投屏多端自适应', icon: 'lucide:smartphone', iconColor: '#7C3AED', iconBg: '#F3E8FF' },
  { title: '数据联动', desc: '组件间数据联动和钻取', icon: 'lucide:link', iconColor: '#0891B2', iconBg: '#ECFEFF' },
  { title: '主题定制', desc: '暗色/亮色/科技风多套主题', icon: 'lucide:palette', iconColor: '#E11D48', iconBg: '#FFF1F2' }
]

const connectors = [
  'MySQL', 'Oracle', 'SQLServer', 'PostgreSQL', 'MongoDB', 'Redis',
  'Kafka', 'Elasticsearch', 'Hive', 'HDFS', 'ClickHouse', 'API'
]

interface FlowStep {
  label: string
  icon: string
  iconColor: string
  iconBg: string
}

const governanceFlow: FlowStep[] = [
  { label: '标准制定', icon: 'lucide:ruler', iconColor: '#0066FF', iconBg: '#EBF5FF' },
  { label: '质量检测', icon: 'lucide:shield-check', iconColor: '#00B96B', iconBg: '#ECFDF5' },
  { label: '安全管控', icon: 'lucide:lock', iconColor: '#FF9500', iconBg: '#FFFBEB' },
  { label: '资产编目', icon: 'lucide:folder-tree', iconColor: '#7C3AED', iconBg: '#F3E8FF' },
  { label: '数据服务', icon: 'lucide:share-2', iconColor: '#0891B2', iconBg: '#ECFEFF' }
]
</script>

<style lang="scss" scoped>
.data-platform-page {
  // navbar-spacer handled by Login.vue
}

.hero-banner {
  height: 320px;
  background: linear-gradient(135deg, #7C3AED, #001B5E);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  .hero-overlay {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 30% 50%, rgba(124, 58, 237, 0.3), transparent 60%);
  }

  .hero-svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0.15;
  }

  .hero-content {
    position: relative;
    z-index: 2;
    text-align: center;
    color: #fff;
  }

  .hero-title {
    font-size: 36px;
    font-weight: 800;
    margin-bottom: 8px;
  }

  .hero-sub {
    font-size: 16px;
    opacity: 0.8;
  }
}

.content-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

.tab-section {
  padding: 64px 24px;
}

.tab-bar {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 48px;
}

.tab-btn {
  padding: 12px 28px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  background: #fff;
  color: #4B5563;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid #E5E7EB;

  &.active {
    background: #0066FF;
    color: #fff;
    border-color: #0066FF;
  }

  &:hover:not(.active) {
    border-color: #D1D5DB;
    color: #374151;
  }
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}

.feature-card {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  .feature-icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
  }

  .feature-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #1F2937;
  }

  .feature-desc {
    font-size: 13px;
    color: #6B7280;
  }
}

// Connector Section
.connector-section {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 32px;

  .connector-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 20px;
    color: #1F2937;
  }
}

.connector-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
}

.connector-item {
  height: 56px;
  background: #F9FAFB;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #4B5563;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background: #EBF5FF;
    color: #0066FF;
  }
}

// Pricing Bar
.pricing-bar {
  background: #F9FAFB;
  border-radius: 12px;
  padding: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;

  .pricing-info {
    .pricing-name {
      font-size: 20px;
      font-weight: 700;
      color: #1F2937;
    }

    .pricing-price-text {
      font-size: 14px;
      color: #6B7280;
      margin-top: 4px;
    }
  }

  .pricing-actions {
    display: flex;
    gap: 12px;
  }

  .btn-outline {
    padding: 12px 24px;
    background: #fff;
    color: #4B5563;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #0066FF;
      color: #0066FF;
    }
  }

  .btn-primary {
    padding: 12px 24px;
    background: #0066FF;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #0044CC;
    }
  }
}

// Governance Flow
.flow-section {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 32px;

  .flow-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 24px;
    color: #1F2937;
  }
}

.flow-steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.flow-step {
  flex: 1;
  text-align: center;

  .flow-icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 8px;
  }

  .flow-label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }
}

.flow-arrow {
  color: #D1D5DB;
  flex-shrink: 0;
}

// Dashboard Preview
.dashboard-preview {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 32px;

  .preview-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 20px;
    color: #1F2937;
  }
}

.preview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.preview-card {
  background: #F9FAFB;
  border-radius: 8px;
  padding: 20px;
  height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .preview-label {
    font-size: 12px;
    color: #6B7280;
  }

  .preview-tag {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;

    &.tag-green {
      background: #ECFDF5;
      color: #00B96B;
    }

    &.tag-brand {
      background: #EBF5FF;
      color: #0066FF;
    }

    &.tag-amber {
      background: #FFFBEB;
      color: #FF9500;
    }
  }

  .preview-bars {
    display: flex;
    align-items: end;
    gap: 4px;
    height: 100px;
    padding: 8px 0;

    .bar {
      flex: 1;
      background: #0066FF;
      border-radius: 4px 4px 0 0;
      min-height: 8px;
    }
  }

  .preview-radar {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
  }

  .preview-funnel {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    height: 100px;
    justify-content: center;

    .funnel-bar {
      height: 16px;
      background: #0066FF;
      border-radius: 3px;
    }
  }
}

// Big Screen Preview
.bigscreen-preview {
  background: #111827;
  border-radius: 12px;
  padding: 32px;
  position: relative;
  overflow: hidden;

  .bigscreen-title {
    font-size: 20px;
    font-weight: 700;
    color: #fff;
    text-align: center;
    margin-bottom: 20px;
  }
}

.bigscreen-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.bigscreen-stat {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;

  .stat-label-light {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 8px;
  }

  .stat-value-cyan {
    font-size: 28px;
    font-weight: 800;
    color: #00D4FF;
  }

  .stat-value-green {
    font-size: 28px;
    font-weight: 800;
    color: #00FFAA;
  }

  .mini-bars {
    display: flex;
    align-items: end;
    gap: 2px;
    height: 40px;
    margin-top: 8px;

    .mini-bar {
      flex: 1;
      border-radius: 2px;

      &.cyan {
        background: #00D4FF;
      }

      &.green {
        background: #00FFAA;
      }
    }
  }

  .stat-sub-row {
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
  }

  .stat-sub-item {
    text-align: center;

    .sub-label {
      font-size: 10px;
      color: rgba(255, 255, 255, 0.4);
    }

    .sub-val {
      font-size: 14px;
      font-weight: 600;

      &.green {
        color: #00FFAA;
      }

      &.amber {
        color: #FF9500;
      }

      &.purple {
        color: #7C3AED;
      }
    }
  }

  .distribution-bars {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
  }

  .dist-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .dist-label {
      width: 60px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.6);
    }

    .dist-bar-bg {
      flex: 1;
      height: 8px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;

      .dist-bar-fill {
        height: 100%;
        border-radius: 4px;

        &.cyan {
          background: #00D4FF;
        }

        &.green {
          background: #00FFAA;
        }

        &.amber {
          background: #FF9500;
        }
      }
    }

    .dist-val {
      font-size: 11px;
      font-weight: 600;

      &.cyan {
        color: #00D4FF;
      }

      &.green {
        color: #00FFAA;
      }

      &.amber {
        color: #FF9500;
      }
    }
  }
}

// CTA Section
.cta-section {
  padding: 80px 24px;
}

.cta-card {
  background: #F9FAFB;
  border-radius: 12px;
  padding: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;

  .cta-content {
    .cta-title {
      font-size: 20px;
      font-weight: 700;
      color: #1F2937;
    }

    .cta-desc {
      font-size: 14px;
      color: #6B7280;
      margin-top: 4px;
    }
  }

  .cta-actions {
    display: flex;
    gap: 12px;
  }

  .btn-outline-cta {
    padding: 12px 24px;
    background: #fff;
    color: #4B5563;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #7C3AED;
      color: #7C3AED;
    }
  }

  .btn-primary-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: #7C3AED;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #6D28D9;
    }
  }
}

@media (max-width: 1024px) {
  .feature-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .connector-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .preview-grid {
    grid-template-columns: 1fr 1fr;
  }

  .bigscreen-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .feature-grid {
    grid-template-columns: 1fr;
  }

  .connector-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .preview-grid {
    grid-template-columns: 1fr;
  }

  .bigscreen-grid {
    grid-template-columns: 1fr;
  }

  .hero-banner {
    height: 240px;

    .hero-title {
      font-size: 28px;
    }

    .hero-svg {
      width: 300px;
      height: 120px;
    }
  }

  .tab-bar {
    flex-wrap: wrap;
  }

  .flow-steps {
    flex-wrap: wrap;
    justify-content: center;

    .flow-arrow {
      display: none;
    }
  }

  .cta-card {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
